import * as React from 'react';
import { Routes, Route } from 'react-router-dom';

import 'regenerator-runtime/runtime';

import loadable, { loadableDelay } from '@common/react/loadable/loadableSettings';

import NotFoundRoute from '@common/react/components/Routes/NotFoundRoute';
import AutocompleteFilter from '@common/react/components/Forms/AutocompleteFilter/AutocompleteFilter';

import DashboardRoute from '@app/components/Routes/DashboardRoute';
import RouteWithFooter from '@app/components/Routes/RouteWithFooter';
import Layout from '@app/components/Layout';
import { getLocationName } from '@app/utils/Utils';
import { Location } from '@app/objects/Location';

/* --------------Admin---------------*/
const Profile = loadable(() =>
	loadableDelay(import(/* webpackChunkName: "ProfilePage" */ '@app/components/Pages/Profile')));
const Dashboard = loadable(() =>
	loadableDelay(import(/* webpackChunkName: "DashboardPage" */ '@app/components/Pages/Admin/Dashboard/Dashboard')));
const ChatsPage = loadable(() =>
	import(/* webpackChunkName: "ChatsPage" */ '@app/components/Pages/Admin/Chats/Chats'));
const Questionnaires = loadable(() =>
	import(/* webpackChunkName: "QuestionnairesPage" */ '@app/components/Pages/Admin/Invites/Questionnaires'));
const ConsentForms = loadable(() =>
	import(/* webpackChunkName: "ConsentFormsPage" */ '@app/components/Pages/Admin/Invites/ConsentForms'));
const Instructions = loadable(() =>
	import(/* webpackChunkName: "InstructionsPage" */ '@app/components/Pages/Admin/Invites/Instructions'));
const Analyzes = loadable(() =>
	import(/* webpackChunkName: "AnalyzesPage" */ '@app/components/Pages/Admin/Analyzes/Analyzes'));
const Appointments = loadable(() =>
	import(/* webpackChunkName: "AppointmentsPage" */ '@app/components/Pages/Admin/Appointments/Appointments'));
const AppointmentViewer = loadable(() =>
	import(/* webpackChunkName: "AppointmentViewerPage" */ '@app/components/Pages/Admin/Appointments/Appointment'));
const Documents = loadable(() =>
	import(/* webpackChunkName: "DocumentsPage" */ '@app/components/Pages/Admin/Documents/Documents'));
const Doctors = loadable(() =>
	import(/* webpackChunkName: "SearchPage" */ '@app/components/Pages/Doctor/Doctors'));
const Prescriptions = loadable(() =>
	import(/* webpackChunkName: "DocumentsPage" */ '@app/components/Pages/Admin/Prescriptions/Prescriptions'));
const Users = loadable(() =>
	import(/* webpackChunkName: "Users" */ '@app/components/Pages/Admin/Users/Users'));
const ShortLinks = loadable(() =>
	import(/* webpackChunkName: "ShortLinks" */ '@app/components/Pages/Admin/ShortLinks/ShortLinks'));
const AuthLinks = loadable(() =>
	import(/* webpackChunkName: "AuthLinks" */ '@common/react/components/Pages/AuthLinks/AuthLinks'));
const AuthLinksFilters = loadable(() =>
	import(/* webpackChunkName: "AuthLinksFilters" */ '@commonTuna/react/components/Pages/Admin/AuthLinks/AuthLinksFilters'));
const UserSessions = loadable(() =>
	import(/* webpackChunkName: "UserSessions" */ '@app/components/Pages/Admin/UserSessions/UserSessions'));
const Notifications = loadable(() =>
	import(/* webpackChunkName: "NotificationsPage" */ '@app/components/Pages/Admin/Notifications/Notifications'));
const SystemNotifications = loadable(() =>
	import(/* webpackChunkName: "SystemNotificationsPage" */ '@app/components/Pages/Admin/Notifications/SystemNotifications'));
const SystemNotificationTypes = loadable(() =>
	import(/* webpackChunkName: "SystemNotificationTypes" */ '@app/components/Pages/Admin/Notifications/SystemNotificationTypes'));
const Pages = loadable(() =>
	import(/* webpackChunkName: "PagesPage" */ '@app/components/Pages/Admin/Pages/Pages'));
const PageEditor = loadable(() =>
	import(/* webpackChunkName: "PageEditorPage" */ '@app/components/Pages/Admin/Pages/PageEditor'));
const Orders = loadable(() =>
	import(/* webpackChunkName: "OrdersPage" */ '@app/components/Pages/Admin/Orders/Orders'));
const Inquiries = loadable(() =>
	import(/* webpackChunkName: "Inquiries" */ '@app/components/Pages/Admin/Inquiries/Inquiries'));
const Bills = loadable(() =>
	import(/* webpackChunkName: "BillsPage" */ '@app/components/Pages/Admin/Bills/Bills'));
const Checkout = loadable(() =>
	import(/* webpackChunkName: "CheckoutPage" */ '@app/components/Pages/Admin/Checkout/Checkout'));
const EmailTemplates = loadable(() =>
	import(/* webpackChunkName: "EmailTemplatesPage" */ '@app/components/Pages/Admin/EmailTemplates/EmailTemplates'));
const MarketingEmails = loadable(() =>
	import(/* webpackChunkName: "MarketingEmailsPage" */ '@app/components/Pages/Admin/MarketingEmails/MarketingEmails'));
const SmsLogs = loadable(() =>
	import(/* webpackChunkName: "SmsLogs" */ '@app/components/Pages/Admin/SmsLogs/SmsLogs'));
const EmailLogs = loadable(() =>
	import(/* webpackChunkName: "EmailLogs" */ '@commonTuna/react/components/Pages/Admin/EmailLogs/EmailLogs'));
const Ordering = loadable(() =>
	import(/* webpackChunkName: "Ordering" */ '@app/components/Pages/Admin/Ordering/Ordering'));
const BaseHostedServices = loadable(() =>
	import(/* webpackChunkName: "BaseHostedServicesPage" */ '@app/components/Pages/Admin/BaseHostedServices/BaseHostedServices'));
const PageAccesses = loadable(() =>
	import(/* webpackChunkName: "PageAccessesPage" */ '@app/components/Pages/Admin/PageAccesses/PageAccesses'));

/* ------------Admin end-------------*/

const Home = loadable(() =>
	loadableDelay(import(/* webpackChunkName: "Home" */ '@app/components/Pages/Home/Home')));
const Login = loadable(() =>
	import(/* webpackChunkName: "LoginPage" */ '@app/components/Pages/Login/Login'));
const Recover = loadable(() =>
	import(/* webpackChunkName: "RecoverPage" */ '@app/components/Pages/Recover/Recover'));
const Confirm = loadable(() =>
	import(/* webpackChunkName: "ConfirmPage" */ '@app/components/Pages/Confirm/Confirm'));
const ContactSupport = loadable(() =>
	import(/* webpackChunkName: "ContactSupportPage" */ '@app/components/Pages/ContactSupport/ContactSupport'));
const Register = loadable(() =>
	import(/* webpackChunkName: "RegisterPage" */ '@app/components/Pages/Register/Register'));
const Confirmation = loadable(() =>
	import(/* webpackChunkName: "ConfirmationPage" */ '@app/components/Pages/Confirmation/Confirmation'));
const ChangeEmailConfirmation = loadable(() =>
	import(/* webpackChunkName: "ChangeEmailConfirmation" */ '@app/components/Pages/Register/ChangeEmailConfirmation'));
const CompanyPortalComponent = loadable(() =>
	import(/* webpackChunkName: "PortalFromBBY" */ '@app/components/Pages/FromBBY/CompanyPortal'));
const DoctorPortalComponent = loadable(() =>
	import(/* webpackChunkName: "DoctorPortalComponent" */ '@app/components/Pages/Doctor/Doctor'));
const DoctorReview = loadable(() =>
	import(/* webpackChunkName: "DoctorReview" */ '@app/components/Pages/Doctor/DoctorReview'));
const Specialties = loadable(() =>
	import(/* webpackChunkName: "Specialties" */ '@app/components/Pages/Specialties/Specialties'));
const Locations = loadable(() =>
	import(/* webpackChunkName: "Locations" */ '@app/components/Pages/Region/Regions'));
const LocationPage = loadable(() =>
	import(/* webpackChunkName: "Region" */ '@app/components/Pages/Region/Region'));
const Specialty = loadable(() =>
	import(/* webpackChunkName: "Specialty" */ '@app/components/Pages/Specialties/Specialty'));
const Specials = loadable(() =>
	import(/* webpackChunkName: "Specials" */ '@app/components/Pages/Specials/Specials'));
const Special = loadable(() =>
	import(/* webpackChunkName: "Special" */ '@app/components/Pages/Specials/Special'));
const Product = loadable(() =>
	import(/* webpackChunkName: "Product" */ '@app/components/Pages/Products/Product'));
const Procedures = loadable(() =>
	import(/* webpackChunkName: "Procedures" */ '@app/components/Pages/Procedures/Procedures'));
const ProcedurePage = loadable(() =>
	import(/* webpackChunkName: "ProcedurePage" */ '@app/components/Pages/Procedures/ProcedurePage'));
const PrivacyPolicy = loadable(() =>
	import(/* webpackChunkName: "PrivacyPolicy" */ '@app/components/Pages/PrivacyPolicy/PrivacyPolicy'));
const RegistrationArticle = loadable(() =>
	import(/* webpackChunkName: "RegistrationArticle" */ '@app/components/Pages/RegistrationArticle/RegistrationArticle'));
const Inquiry = loadable(() =>
	import(/* webpackChunkName: "Inquiry" */ '@app/components/Pages/Inquiry/Inquiry'));
const ExpiredLink = loadable(() =>
	import(/* webpackChunkName: "ExpiredLink" */ '@app/components/Pages/ExpiredLink/ExpiredLink'));
const ShoppingCart = loadable(() =>
	import(/* webpackChunkName: "ShoppingCart" */ '@app/components/Pages/ShoppingCart/ShoppingCart'));
const QuickPay = loadable(() =>
	import(/* webpackChunkName: "QuickPayPage" */ '@app/components/Pages/QuickPay/QuickPay'));
const ErrorPage = loadable(() =>
	import(/* webpackChunkName: "ErrorPage" */ '@common/react/components/Pages/ErrorPage/ErrorPage'));
const PaymentsProcessing = loadable(() =>
	import(/* webpackChunkName: "PaymentsProcessing" */ '@app/components/Pages/PaymentsProcessing/PaymentsProcessing'));
const Clinics = loadable(() =>
	import(/* webpackChunkName: "ClinicsPage" */ '@app/components/Pages/Clinics/Clinics'));
const ClinicPortalComponent = loadable(() =>
	import(/* webpackChunkName: "ClinicPortalComponent" */ '@app/components/Pages/Clinics/ClinicPortalComponent'));
const UserPage = loadable(() =>
	import(/* webpackChunkName: "UserPage" */ '@app/components/Pages/Admin/Users/User'));

const dashboardRoutes = [
	{
		path: '/dashboard',
		component: Dashboard,
	},
	{
		path: '/profile',
		component: Profile,
		title: 'Profile',
	},
	{
		title: 'Chats',
		component: ChatsPage,
		path: '/chats',
	},
	{
		title: 'Questionnaires',
		component: Questionnaires,
		path: '/questionnaires/:page?',
	},
	{
		title: 'Consent|Medical Documents',
		component: ConsentForms,
		path: '/consent-forms/:page?',
	},
	{
		title: 'Patient Instructions',
		component: Instructions,
		path: '/instructions/:page?',
	},
	{
		title: 'Lab. Orders',
		component: Analyzes,
		path: '/analysis-list/:page?',
	},
	{
		title: 'Appointments',
		component: Appointments,
		path: '/appointment-list/:page?',
	},
	{
		title: 'Appointment',
		component: AppointmentViewer,
		path: '/appointment/:id',
	},
	{
		title: 'Health Records',
		component: Documents,
		path: '/document-list/:page?',
	},
	{
		title: 'Prescriptions',
		component: Prescriptions,
		path: '/prescriptions-list/:page?',
	},
	{
		title: 'Users',
		component: Users,
		path: '/user-list/:page?',
	},
	{
		title: 'Short Links',
		component: ShortLinks,
		path: '/shortLink-list/:page?',
	},
	{
		title: 'Entry Log Journal',
		component: UserSessions,
		path: '/user-session-list/:page?',
	},
	{
		title: 'Notifications',
		component: Notifications,
		path: '/notifications/:page?',
	},
	{
		title: 'Notifications',
		component: SystemNotifications,
		path: '/system-notifications/:page?',
	},
	{
		title: 'Notification Types',
		component: SystemNotificationTypes,
		path: '/notification-types/:page?',
	},
	{
		title: 'Pages',
		component: Pages,
		path: '/page-list/:page?',
	},
	{
		title: 'Page Editor',
		component: PageEditor,
		path: '/page-editor/:id',
	},
	{
		title: 'Inquiries',
		component: Inquiries,
		path: '/inquiry-list/:page?',
	},
	{
		title: 'Purchases',
		component: Orders,
		path: '/orders/:page?',
	},
	{
		title: 'Bills',
		component: Bills,
		path: '/bills/:page?',
	},
	{
		title: 'Checkout',
		component: Checkout,
		path: '/checkout/:uid?',
	},
	{
		title: 'Email Templates',
		component: EmailTemplates,
		path: '/email-templates',
	},
	{
		title: 'Marketing Emails',
		component: MarketingEmails,
		path: '/marketing-emails/:page?',
	},
	{
		title: 'Sms Logs',
		component: SmsLogs,
		path: '/smsLogs/:page?',
	},
	{
		title: 'Email Logs',
		component: () => <EmailLogs
			renderFilters={(filters, values, handleChange) => <AutocompleteFilter<Location>
				type="locationList"
				renderTitle={(location: Location) => getLocationName(location, true, true)}
				param="locationId"
				antdProps={{
					styles: {
						popup: {
							root: { minWidth: 'min(400px, 70vw)' },
						},
					},
				}}
				handleChangeAndRefresh={handleChange}
				value={values?.locationName}
				placeholder="Clinic..."
				loadOnFocus
			/>}
			showUser
		/>,
		path: '/emailLogs',
	},
	{
		title: 'Hosted Services',
		component: BaseHostedServices,
		path: '/base-hosted-service-list/:page?',
	},
	{
		title: 'Page Accesses',
		component: PageAccesses,
		path: '/page-access-list/:page?',
	},
	{
		title: 'Auth Links',
		component: (props) => <AuthLinks filters={<AuthLinksFilters />} />,
		path: '/authLink-list/:page?',
	},
	{
		component: (props) => <UserPage />,
		path: '/user/:id?',
	},
];

export const baseRoutes = (<>
	<Route path="/" element={<RouteWithFooter />}>
		<Route path="/" element={<Home />} />
		<Route path="/login" element={<Login />} />
		<Route path="/registration" element={<Register />} />
		<Route path="/confirmation" element={<Confirmation />} />
		<Route path="/recover" element={<Recover />} />
		<Route path="/confirm" element={<Confirm />} />
		<Route path="/change-email" element={<ChangeEmailConfirmation />} />
		<Route path="/doctors" element={<Doctors />} />
		<Route path="/doctors/:path?" element={<DoctorPortalComponent />} />
		<Route path="/doctor-review/:path?" element={<DoctorReview />} />
		<Route path="/contact-support" element={<ContactSupport />} />
		<Route path="/company/:path?" element={<CompanyPortalComponent />} />
		<Route path="/privacy-policy" element={<PrivacyPolicy />} />
		<Route path="/about-registration" element={<RegistrationArticle />} />
		<Route path="/specialties" element={<Specialties />} />
		<Route path="/locations" element={<Locations />} />
		<Route path="/locations/*" element={<LocationPage />} />
		<Route path="/procedures" element={<Procedures />} />
		<Route path="/procedures/*" element={<ProcedurePage />} />
		<Route path="/specialties/*" element={<Specialty />} />
		<Route path="/inquiry" element={<Inquiry />} />
		<Route path="/specials/*" element={<Special />} />
		<Route path="/specials" element={<Specials />} />
		<Route path="/product/*" element={<Product />} />
		<Route path="/shopping-cart" element={<ShoppingCart />} />
		<Route path="/ordering" element={<Ordering />} />
		<Route path="/expiredLink" element={<ExpiredLink />} />
		<Route path="/quick-pay" element={<QuickPay />} />
		<Route path="/payments-processing" element={<PaymentsProcessing />} />
		<Route path="/clinics" element={<Clinics />} />
		<Route path="/clinics/*" element={<ClinicPortalComponent />} />
	</Route>
	{dashboardRoutes.map((item) => <Route
		key={item.path}
		path={item.path}
		element={<DashboardRoute component={item.component} title={item.title} />}
	/>)}
	<Route
		path="/error"
		element={<NotFoundRoute
			title="Error"
			component={ErrorPage}
			loginRoute={DashboardRoute}
			openRoute={(props) => <RouteWithFooter {...props} withHeader />}
		/>}
	/>
	<Route
		path="*"
		element={<NotFoundRoute
			loginRoute={DashboardRoute}
			openRoute={(props) => <RouteWithFooter {...props} withHeader />}
		/>}
	/>
</>);

export const clientRoutes = (<Route element={<Layout />}>
	{baseRoutes}
</Route>);

export const routes = (<Layout>
	<Routes>
		{baseRoutes}
	</Routes>
</Layout>);
